import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ContactFAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());

  const faqCategories = [
    {
      category: 'General Questions',
      icon: 'HelpCircle',
      questions: [
        {
          id: 'general-1',
          question: 'What is Cynizee and how does it help families?',
          answer: `Cynizee is a family-first SaaS marketing platform that transforms chaotic family life into organized harmony. We help families build healthy habits without daily battles through our innovative Lock Mode feature, task management, and child-friendly interfaces.\n\nOur platform bridges the gap between parental control and child empowerment, creating a structured yet flexible environment where every family member can thrive.`
        },
        {
          id: 'general-2',
          question: 'When will Cynizee be available?',
          answer: `We're currently in development and building our waitlist community. Early access will be available to waitlist members first, followed by a broader public launch.\n\nJoin our waitlist to get notified about beta testing opportunities and be among the first families to experience the transformation from chaos to calm.`
        },
        {
          id: 'general-3',question: 'How much will Cynizee cost?',
          answer: `We offer two family-focused plans that both include our signature Lock Mode feature:\n\n• Family Starter ($12.99/month) - Up to 5 family members with essential lock features\n• Family Harmony ($19.99/month) - Up to 8 family members with advanced lock capabilities\n\nBoth plans include Lock Mode as the core feature - the main reason families choose Cynizee. Waitlist members receive special early-bird pricing and exclusive discounts.`
        }
      ]
    },
    {
      category: 'Partnership Opportunities',icon: 'Handshake',
      questions: [
        {
          id: 'partnership-1',question: 'Do you work with educators and therapists?',
          answer: `Absolutely! We actively collaborate with educators, family therapists, and child development specialists who understand the importance of structured family routines.\n\nWe offer special partnership programs, referral opportunities, and professional resources for those who work with families in need of organizational support.`
        },
        {
          id: 'partnership-2',question: 'How can I become a Cynizee partner?',
          answer: `We welcome partnerships with organizations that share our family-first values. This includes educational institutions, therapy practices, family service organizations, and complementary technology providers.\n\nContact us with "Partnership Opportunities" selected to discuss how we can work together to support more families.`
        }
      ]
    },
    {
      category: 'Accessibility Support',icon: 'Accessibility',
      questions: [
        {
          id: 'accessibility-1',question: 'Is Cynizee designed for children with special needs?',
          answer: `Yes! Accessibility and inclusion are core to our design philosophy. Cynizee includes features specifically designed for children with learning differences, ADHD, autism, and other special needs.\n\nOur read-aloud features, visual task indicators, and customizable interfaces ensure that every child can successfully use and benefit from our platform.`
        },
        {
          id: 'accessibility-2',question: 'What accessibility features does Cynizee include?',
          answer: `Cynizee is built with WCAG AA compliance and includes:\n• Screen reader compatibility\n• High contrast mode support\n• Keyboard navigation\n• Customizable text sizes\n• Audio task instructions\n• Visual progress indicators\n• Simple, clear interface design\n\nWe continuously work with accessibility experts and families to improve our inclusive design.`
        }
      ]
    },
    {
      category: 'Technical Support',icon: 'Settings',
      questions: [
        {
          id: 'technical-1',question: 'What devices will Cynizee work on?',answer: `Cynizee is designed to work seamlessly across all your family's devices:\n• iOS and Android smartphones and tablets\n• Desktop computers (Windows, Mac, Linux)\n• Web browsers with responsive design\n• Smart home integration capabilities\n\nOur goal is to meet your family where you are, on whatever devices you already use.`
        },
        {
          id: 'technical-2',
          question: 'How do I report a bug or technical issue?',
          answer: `We take technical issues seriously, especially when they affect your family's daily routine. To report issues:\n\n1. Use our contact form and select "Technical Issues"\n2. Mark urgency as "High" or "Urgent" for family-critical problems\n3. Include device information and steps to reproduce the issue\n4. Email support@cynizee.com directly for immediate concerns\n\nWe prioritize family-affecting issues during our business hours: Monday-Friday, 9:00 AM - 7:00 PM Central Time.`
        }
      ]
    }
  ];

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded?.has(itemId)) {
      newExpanded?.delete(itemId);
    } else {
      newExpanded?.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const filteredCategories = faqCategories?.map(category => ({
    ...category,
    questions: category?.questions?.filter(
      q => 
        q?.question?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        q?.answer?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
  }))?.filter(category => category?.questions?.length > 0);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-heading font-bold text-foreground mb-4">
          Frequently Asked Questions
        </h3>
        <p className="text-text-secondary mb-6">
          Find quick answers to common questions. Can't find what you're looking for? Use the contact form above.
        </p>
        
        <Input
          type="search"
          placeholder="Search FAQ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="mb-6"
        />
      </div>
      {filteredCategories?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-heading font-semibold text-foreground mb-2">
            No results found
          </h4>
          <p className="text-text-secondary">
            Try different search terms or browse all categories below.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {filteredCategories?.map((category) => (
            <div key={category?.category}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={category?.icon} size={18} className="text-primary" />
                </div>
                <h4 className="text-lg font-heading font-semibold text-foreground">
                  {category?.category}
                </h4>
              </div>
              
              <div className="space-y-3">
                {category?.questions?.map((item) => (
                  <div
                    key={item?.id}
                    className="bg-card rounded-lg border border-border overflow-hidden"
                  >
                    <button
                      onClick={() => toggleExpanded(item?.id)}
                      className="w-full px-6 py-4 text-left hover:bg-muted/50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset"
                    >
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium text-foreground pr-4">
                          {item?.question}
                        </h5>
                        <Icon
                          name="ChevronDown"
                          size={20}
                          className={`text-text-secondary transition-transform duration-200 flex-shrink-0 ${
                            expandedItems?.has(item?.id) ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                    </button>
                    
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedItems?.has(item?.id) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-6 pb-4">
                        <div className="pt-2 border-t border-border">
                          {item?.answer?.split('\n')?.map((paragraph, index) => (
                            <p key={index} className="text-text-secondary text-sm leading-relaxed mb-2 last:mb-0">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Contact CTA */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6 border border-primary/10">
        <div className="text-center">
          <Icon name="MessageCircle" size={32} className="text-primary mx-auto mb-4" />
          <h4 className="text-lg font-heading font-semibold text-foreground mb-2">
            Still have questions?
          </h4>
          <p className="text-text-secondary mb-4">
            Our team is here to help during business hours (Mon-Fri, 9:00 AM - 7:00 PM CT). 
            We typically respond to emails within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:support@cynizee.com"
              className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200 text-sm font-medium"
            >
              <Icon name="Mail" size={16} className="mr-2" />
              Email Us Directly
            </a>
            <button
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors duration-200 text-sm font-medium"
            >
              <Icon name="MessageSquare" size={16} className="mr-2" />
              Use Contact Form
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFAQ;